import { shipToAddressText, shipToContactText } from './shippingQuote';
import { formatShipToLocation } from '../../utils/shipToLocation';
import { buildAssemblyChangeLogEntry } from '../AssemblyPage/assemblyScheduleChangeLogUtils';

export const buildShippingChangeLogEntry = (values) => {
  const { ProjectID, ...entry } = buildAssemblyChangeLogEntry(values);
  return { ProjectId: ProjectID, ...entry };
};

const field = (key, label, type = 'text', options) => ({ key, label, type, options });
const statusDefaults = { CheckedIn: 'No', Packed: 'Not Started', Audit: 'Not Started', Shipped: 'No' };

export const SHIPPING_FIELDS = [
  field('ExpectedArrivalTime', 'Expected Arrival Time'),
  field('CheckedIn', 'Checked In', 'select', ['Yes', 'No']),
  field('Packed', 'Packed', 'select', ['Not Started', 'In Progress', 'Complete', 'N/A']),
  field('Audit', 'Audit', 'select', ['Not Started', 'Complete', 'N/A']),
  field('Shipped', 'Shipped', 'select', ['Yes', 'No']),
  field('DeliveryDate', 'Delivery Date', 'date'),
  field('QuoteLookup', 'Quote Lookup', 'select', ['1', '2', '3']),
  field('Miles', 'Miles', 'number'),
  field('Teams', 'Teams'),
  { ...field('TransporterOne', 'Transporter 1', 'select'), dropdownType: 'Transporter' }, field('TransporterQuoteOne', 'Quote 1', 'number'),
  { ...field('TransporterTwo', 'Transporter 2', 'select'), dropdownType: 'Transporter' }, field('TransporterQuoteTwo', 'Quote 2', 'number'),
  { ...field('TransporterThree', 'Transporter 3', 'select'), dropdownType: 'Transporter' }, field('TransporterQuoteThree', 'Quote 3', 'number'),
  field('FinalQuote', 'Final Quote', 'number'), field('Notes', 'Notes', 'textarea'),
];

const identity = [field('JobNumber', 'Job Number'), field('ProjectName', 'Job Name'), field('Destination', 'Destination')];
const shipDate = field('CurrentPlanShipDate', 'Scheduled Ship Date', 'date');
const actualShipDate = field('ActualShipDate', 'Actual Ship Date', 'date');
const transportMode = field('TransportMode', 'Transport Mode');
const budget = field('Budget', 'Budget', 'number');
const shippingField = (key) => SHIPPING_FIELDS.find((item) => item.key === key);
export const shippingTransporterOptions = (row) => ['One', 'Two', 'Three'].map((suffix, index) => {
  const value = String(index + 1);
  const name = String(row[`Transporter${suffix}`] ?? '').trim();
  return { value, label: name ? `${value} - ${name}` : value };
});
export const SHIPPING_COLUMNS = {
  display: [...identity, transportMode, shipDate, actualShipDate, shippingField('ExpectedArrivalTime'), field('PaymentTerms', 'Payment Terms'), ...['Packed', 'Audit', 'Shipped'].map(shippingField)],
  'end-of-shift': [...identity, field('TransportMode', 'Truck Type'), shipDate, actualShipDate, shippingField('DeliveryDate'), shippingField('Shipped'), field('SelectedTransporter', 'Transporter'), field('FreightCost', 'Freight Cost', 'currency')],
  'shipping-details': [identity[0], identity[1], shipDate, transportMode, field('PaymentTerms', 'Payment Terms'), field('ShipToDetails', 'Ship To', 'multiline'), field('ShipToContact', 'Ship To Contact', 'multiline'), field('Budget', 'Freight Amount (Budget)', 'currency'), shippingField('Notes'), field('DraftQuote', 'Draft for Quote')],
  'data-entry': [identity[0], field('ModelNumber', 'Model'), identity[1], identity[2], shipDate, actualShipDate, ...['ExpectedArrivalTime', 'CheckedIn', 'Packed', 'Audit', 'Shipped', 'DeliveryDate', 'QuoteLookup'].map(shippingField), shippingField('Miles'), budget, transportMode, ...['Teams', 'TransporterOne', 'TransporterQuoteOne', 'TransporterTwo', 'TransporterQuoteTwo', 'TransporterThree', 'TransporterQuoteThree', 'FinalQuote'].map(shippingField), field('CostPerMile', '$/Mile', 'currency'), shippingField('Notes')].map((column) => ['Budget', 'TransporterQuoteOne', 'TransporterQuoteTwo', 'TransporterQuoteThree'].includes(column.key) ? { ...column, displayType: 'currency' } : column),
};

export const unwrapShippingPayload = (payload) => payload?.data ?? payload?.value ?? payload?.items ?? payload;
export const shippingRecords = (payload) => {
  const value = unwrapShippingPayload(payload);
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  if (value.ProjectId != null || value.ProjectID != null || Object.keys(value).some((key) => key.startsWith('ShipTo'))) return [value];
  return Object.values(value).filter((item) => item && typeof item === 'object');
};
export const shippingProjectId = (record) => record?.ProjectId ?? record?.ProjectID ?? record?.projectId;
export const shippingRecordId = (record) => record?.Id ?? record?.ID ?? record?.id;
export const destinationText = formatShipToLocation;
export const inputValue = (value, type) => {
  const text = String(value ?? '');
  if (type === 'date') return text.slice(0, 10);
  if (type === 'datetime-local') return text.replace(' ', 'T').slice(0, 16);
  return text;
};

export function normalizeShippingSchedule(payload, shipToPayload) {
  const shipToByProject = new Map(shippingRecords(shipToPayload)
    .filter((record) => shippingProjectId(record) != null)
    .map((record) => [String(shippingProjectId(record)), record]));
  return shippingRecords(payload).map((entry) => {
    const generalProject = entry.GeneralProjectInfo ?? entry.generalProjectInfo;
    const job = generalProject ?? entry;
    // GeneralProjectInfo.ID is the general-project primary key, not a shipping record ID.
    const projectId = generalProject ? job.ID ?? job.Id ?? shippingProjectId(job) : shippingProjectId(job) ?? job.ID ?? job.Id;
    const addresses = shippingRecords(entry.SoldToShipTo ?? entry.soldToShipTo);
    const address = shipToByProject.get(String(projectId)) ?? addresses.find((item) => destinationText(item)) ?? addresses.find((item) => shipToAddressText(item) || shipToContactText(item));
    const priceSection = entry.PriceBreakdown ?? entry.priceBreakdown;
    const price = (priceSection?.FreightAmount != null ? [priceSection] : shippingRecords(priceSection)).find((item) => item.FreightAmount != null);
    const shipToDestination = destinationText(address);
    const fallbackLocation = String(job.JobLocation ?? '').trim();
    const destinationFromGeneralProject = !shipToDestination && Boolean(fallbackLocation);
    const destination = shipToDestination || (fallbackLocation ? `${fallbackLocation}*` : '');
    return { ...job, ProjectId: projectId, GeneralProjectId: projectId, Destination: destination, DestinationFromGeneralProject: destinationFromGeneralProject, ShipToDetails: shipToAddressText(address), ShipToContact: shipToContactText(address), Budget: price?.FreightAmount ?? '' };
  }).filter((job) => job.ProjectId != null);
}

export function shippingGeneralProjectId(row) {
  const projectId = row.GeneralProjectId ?? row.ProjectId;
  const recordProjectId = shippingProjectId(row.shippingRecord);
  if (projectId == null || String(projectId).trim() === '' || String(projectId) !== String(row.ProjectId) || (recordProjectId != null && String(recordProjectId) !== String(projectId))) {
    throw new Error(`Project identity mismatch for ${row.JobNumber || 'this job'}. Refresh before saving.`);
  }
  return projectId;
}

export function mergeShippingRows(jobs, records) {
  const recordsByProject = new Map(records.map((record) => [String(shippingProjectId(record)), record]));
  return jobs.map((job) => {
    const record = recordsByProject.get(String(job.ProjectId));
    // Do not allow the shipping record's Id or Notes to replace project identity or schedule notes.
    const row = { ...job, TransportMode: job.TransportType ?? '', Budget: job.Budget ?? '', Notes: '', shippingRecord: record };
    SHIPPING_FIELDS.forEach(({ key }) => {
      if (record && Object.prototype.hasOwnProperty.call(record, key)) row[key] = record[key];
    });
    row.DeliveryDate = job.ScheduledArrivalDate ?? '';
    Object.entries(statusDefaults).forEach(([key, value]) => {
      if (String(row[key] ?? '').trim() === '') row[key] = value;
    });
    row.Destination = job.Destination || '';
    const quoteSuffix = { '1': 'One', '2': 'Two', '3': 'Three' }[String(row.QuoteLookup ?? '').trim()];
    row.SelectedTransporter = row.TransportMode === 'CPU' ? 'Customer Pickup' : quoteSuffix ? row[`Transporter${quoteSuffix}`] : '';
    row.FreightCost = row.TransportMode === 'CPU' ? 0 : quoteSuffix ? row[`TransporterQuote${quoteSuffix}`] : row.FinalQuote;
    const miles = Number(row.Miles);
    const selectedQuote = quoteSuffix ? String(row[`TransporterQuote${quoteSuffix}`] ?? '').trim() : '';
    const quoteAmount = Number(selectedQuote);
    row.CostPerMile = Number.isFinite(miles) && miles > 0 && selectedQuote !== '' && Number.isFinite(quoteAmount) && quoteAmount >= 0 ? quoteAmount / miles : '';
    return row;
  });
}

export function buildShippingMutation(row, draft) {
  const changes = {};
  SHIPPING_FIELDS.forEach(({ key, type }) => {
    if (inputValue(row[key], type) === inputValue(draft[key], type)) return;
    const value = String(draft[key] ?? '').trim();
    if (key === 'QuoteLookup' && value !== '' && !['1', '2', '3'].includes(value)) throw new Error('Quote Lookup must be 1, 2, or 3.');
    if (type === 'number' && value !== '' && (!Number.isFinite(Number(value)) || Number(value) < 0)) throw new Error(`${key} must be a non-negative number.`);
    changes[key] = type === 'datetime-local' ? value.replace('T', ' ') : value;
  });
  const id = shippingRecordId(row.shippingRecord);
  if (row.shippingRecord && id == null) throw new Error('This shipping record has no Id. Refresh before editing it.');
  return {
    method: id != null ? 'patch' : 'post',
    changes,
    payload: { ...(id != null ? { Id: id } : {}), ProjectId: row.ProjectId, ...changes },
  };
}
